<template>
  <div class="watch-page">
    <div class="watch-hero" v-if="footballLiveMatch.home_team_name">
      <div class="league-row">
        <img
          v-if="footballLiveMatch.league_logo"
          :src="footballLiveMatch.league_logo"
          alt=""
          class="league-logo"
        />
        <span>{{ footballLiveMatch.league_name }}</span>
        <span class="live-badge" v-if="isLive">
          <span class="pulse"></span>
          LIVE
        </span>
      </div>

      <div class="teams-row">
        <div class="team">
          <img
            :src="footballLiveMatch.home_team_logo"
            :alt="footballLiveMatch.home_team_name"
          />
          <strong>{{ footballLiveMatch.home_team_name }}</strong>
        </div>
        <div class="vs">VS</div>
        <div class="team">
          <img
            :src="footballLiveMatch.away_team_logo"
            :alt="footballLiveMatch.away_team_name"
          />
          <strong>{{ footballLiveMatch.away_team_name }}</strong>
        </div>
      </div>
    </div>

    <div class="player-card">
      <div class="player-wrap">
        <video
          ref="videoPlayer"
          class="live-video"
          controls
          autoplay
          playsinline
          muted
        ></video>
        <div class="player-overlay" v-if="statusText">
          <div class="overlay-inner">
            <Loading
              v-if="isLoadingStatus"
              color="#e7b534"
              size="24"
              class="mb-2"
            />
            <p>{{ statusText }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="servers-section" v-if="servers.length">
      <div class="section-head">
        <h3>
          {{
            $store.state.language === "en"
              ? "Stream servers"
              : "ကြည့်ရှုရန် ချန်နယ်"
          }}
        </h3>
        <span>{{ servers.length }}</span>
      </div>
      <div class="server-grid">
        <button
          v-for="(server, index) in servers"
          :key="index"
          type="button"
          class="server-chip"
          :class="{ active: activeServer === index }"
          @click="playServer(server, index)"
        >
          <i class="fas fa-play-circle"></i>
          <span>{{ serverLabel(server, index) }}</span>
        </button>
      </div>
      <p class="hint">
        {{
          $store.state.language === "en"
            ? "If one server fails, try another quality/channel."
            : "မရပါက အခြားချန်နယ်ကို စမ်းကြည့်ပါ။"
        }}
      </p>
    </div>

    <div class="empty-stream" v-else-if="loaded">
      <i class="fas fa-video-slash mb-2"></i>
      <p>
        {{
          $store.state.language === "en"
            ? "Stream not available yet."
            : "တိုက်ရိုက်လွှင့်မှု မရရှိသေးပါ။"
        }}
      </p>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { Loading } from "vant";

const HLS_CDN =
  "https://cdn.jsdelivr.net/npm/hls.js@1.5.17/dist/hls.min.js";

function loadHlsScript() {
  if (window.Hls) return Promise.resolve(window.Hls);
  if (window.__hlsLoading) return window.__hlsLoading;

  window.__hlsLoading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = HLS_CDN;
    script.async = true;
    script.onload = () => resolve(window.Hls);
    script.onerror = () => reject(new Error("Failed to load HLS.js"));
    document.head.appendChild(script);
  });

  return window.__hlsLoading;
}

export default {
  components: { Loading },
  data() {
    return {
      footballLiveMatch: {},
      loaded: false,
      activeServer: 0,
      statusText: "",
      hls: null,
    };
  },
  computed: {
    servers() {
      return Array.isArray(this.footballLiveMatch?.servers)
        ? this.footballLiveMatch.servers.filter((s) => s?.stream_url || s?.link)
        : [];
    },
    isLive() {
      return this.footballLiveMatch?.match_status?.toLowerCase() === "live";
    },
    isLoadingStatus() {
      const text = (this.statusText || "").toLowerCase();
      return (
        text.includes("loading") ||
        text.includes("fetching") ||
        text.includes("trying") ||
        text.includes("recovering")
      );
    },
  },
  methods: {
    ...mapActions(["fetchUser"]),
    serverLabel(server, index) {
      const name = server?.name || "Server";
      return `${name} ${index + 1}`;
    },
    getStreamUrl(server) {
      return server?.stream_url || server?.link || "";
    },
    destroyPlayer() {
      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }
      const video = this.$refs.videoPlayer;
      if (video) {
        video.removeAttribute("src");
        video.load();
      }
    },
    async playServer(server, index = 0) {
      const streamUrl = this.getStreamUrl(server);
      if (!streamUrl) {
        this.statusText = "Stream URL missing.";
        return;
      }

      this.activeServer = index;
      this.statusText =
        this.$store.state.language === "en"
          ? "Loading stream..."
          : "စတင်ဖွင့်နေပါသည်...";
      this.destroyPlayer();

      await this.$nextTick();
      const video = this.$refs.videoPlayer;
      if (!video) return;

      try {
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = streamUrl;
          await video.play().catch(() => {});
          this.statusText = "";
          return;
        }

        const Hls = await loadHlsScript();
        if (!Hls?.isSupported()) {
          this.statusText =
            this.$store.state.language === "en"
              ? "HLS is not supported in this browser."
              : "ဤဘရောက်ဇာတွင် မပံ့ပိုးပါ။";
          return;
        }

        this.hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
        });

        this.hls.loadSource(streamUrl);
        this.hls.attachMedia(video);

        this.hls.on(Hls.Events.MANIFEST_PARSED, async () => {
          this.statusText = "";
          try {
            await video.play();
          } catch (e) {
            this.statusText =
              this.$store.state.language === "en"
                ? "Tap play to start."
                : "Play ကိုနှိပ်၍ ကြည့်ပါ။";
          }
        });

        this.hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data?.fatal) return;

          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            this.statusText =
              this.$store.state.language === "en"
                ? "Media error. Recovering..."
                : "ပြန်လည်ကြိုးစားနေပါသည်...";
            this.hls.recoverMediaError();
            return;
          }

          this.statusText =
            this.$store.state.language === "en"
              ? "Stream failed. Trying another server..."
              : "မရသေးပါ။ အခြားချန်နယ် စမ်းနေသည်...";
          this.destroyPlayer();
          this.tryNextServer(index);
        });
      } catch (error) {
        console.log(error);
        this.statusText =
          this.$store.state.language === "en"
            ? "Unable to play this stream."
            : "ဤချန်နယ်ကို ဖွင့်၍မရပါ။";
      }
    },
    tryNextServer(currentIndex) {
      const next = currentIndex + 1;
      if (next < this.servers.length) {
        setTimeout(() => this.playServer(this.servers[next], next), 800);
      } else {
        this.statusText =
          this.$store.state.language === "en"
            ? "All servers failed. Try again later."
            : "ချန်နယ်အားလုံး မရပါ။ နောက်မှ ထပ်ကြိုးစားပါ။";
      }
    },
    async fetchMatches() {
      try {
        this.statusText =
          this.$store.state.language === "en"
            ? "Fetching match..."
            : "ပွဲအချက်အလက် ရယူနေသည်...";
        const res = await axios.get(
          "https://footballapi.spacetechmm.com/free-live-matches",
          {
            transformRequest: [
              (data, headers) => {
                delete headers.Authorization;
                return data;
              },
            ],
          }
        );
        const matches = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || [];

        const tab = this.$route.query.tab || "Live";
        const matchTime = this.$route.query.match_time;
        const index = Number(this.$route.query.index) || 0;

        const filtered = matches.filter((match) => {
          const live = match.match_status?.toLowerCase() === "live";
          return tab === "Live" ? live : !live;
        });

        if (matchTime) {
          const found = matches.find(
            (m) => String(m.match_time) === String(matchTime)
          );
          this.footballLiveMatch = found || filtered[index] || {};
        } else {
          this.footballLiveMatch = filtered[index] || {};
        }

        if (this.servers[0]) {
          await this.playServer(this.servers[0], 0);
        } else {
          this.statusText =
            this.$store.state.language === "en"
              ? "Stream not available yet."
              : "တိုက်ရိုက်လွှင့်မှု မရရှိသေးပါ။";
        }
      } catch (error) {
        console.log(error);
        this.statusText =
          this.$store.state.language === "en"
            ? "Failed to load match data."
            : "ပွဲအချက်အလက် မရရှိပါ။";
      } finally {
        this.loaded = true;
      }
    },
  },
  async mounted() {
    const authUser = await this.fetchUser();
    if (!authUser) {
      this.$router.push("/auth/login");
      return;
    }
    this.fetchMatches();
  },
  beforeDestroy() {
    this.destroyPlayer();
  },
};
</script>

<style scoped>
.watch-page {
  min-height: calc(100vh - 120px);
  padding: 0.85rem 0.85rem 5.5rem;
  background: #0c0c0c;
}

.watch-hero {
  color: #f0e6c8;
  margin-bottom: 0.9rem;
  background: #1c1c1c;
  border-radius: 16px;
  padding: 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.league-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0.9rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #d8c48a;
}

.league-logo {
  width: 20px;
  height: 20px;
  object-fit: contain;
  background: #111;
  border-radius: 4px;
}

.live-badge {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(220, 38, 38, 0.18);
  color: #ff6b6b;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.pulse {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ff3b3b;
  animation: pulse-dot 1.6s infinite;
}

@keyframes pulse-dot {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 59, 59, 0.55);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 59, 59, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 59, 59, 0);
  }
}

.teams-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
}

.team {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  min-width: 0;
}

.team img {
  width: 58px;
  height: 58px;
  object-fit: contain;
  background: #111;
  border-radius: 14px;
  padding: 6px;
}

.team strong {
  font-size: 0.86rem;
  line-height: 1.25;
  color: #f0e6c8;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.vs {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: linear-gradient(180deg, #f7d56a, #e7b534);
  color: #1a1408;
  font-size: 0.72rem;
  font-weight: 800;
  box-shadow: 0 6px 14px rgba(231, 181, 52, 0.22);
}

.player-card {
  background: #0b0f0c;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.player-wrap {
  position: relative;
  width: 100%;
  background: #000;
}

.live-video {
  width: 100%;
  min-height: 220px;
  max-height: 360px;
  display: block;
  background: #000;
}

.player-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  pointer-events: none;
}

.overlay-inner {
  text-align: center;
  padding: 1rem;
}

.overlay-inner p {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
}

.servers-section {
  margin-top: 1rem;
  background: #1c1c1c;
  border-radius: 16px;
  padding: 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.section-head h3 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 800;
  color: #f0e6c8;
}

.section-head span {
  min-width: 24px;
  height: 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(231, 181, 52, 0.15);
  color: #f7d56a;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0 7px;
}

.server-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
}

.server-chip {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #161616;
  color: #f0e6c8;
  border-radius: 12px;
  padding: 0.7rem 0.65rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 0.8rem;
  font-weight: 700;
}

.server-chip i {
  color: #e7b534;
}

.server-chip.active {
  background: linear-gradient(180deg, #f7d56a, #e7b534);
  border-color: #e7b534;
  color: #1a1408;
}

.server-chip.active i {
  color: #1a1408;
}

.hint {
  margin: 0.75rem 0 0;
  font-size: 0.78rem;
  color: #b8b3a8;
}

.empty-stream {
  margin-top: 1rem;
  background: #1c1c1c;
  border-radius: 16px;
  padding: 1.4rem 1rem;
  text-align: center;
  color: #b8b3a8;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.empty-stream i {
  font-size: 1.4rem;
  color: #e7b534;
}

.empty-stream p {
  margin: 0;
  font-weight: 600;
}
</style>
